import { z } from "zod";
import { ColorSchema } from "./primitives";
import { TextBodySchema } from "./text";

/**
 * Drawing layer. Lives in the room (Durable Object) and layers.json, not in deck.json.
 * All coordinates are slide units (pt).
 */
const AnnotationBase = z.object({
  id: z.string(),
  authorId: z.string(),
  createdAt: z.number(),
  /** named place on the page: adding an annotation with a slot replaces the one already there (e.g. an app's live overlay) */
  slot: z.string().max(100).optional(),
});

/** Freehand stroke. The marker look is just a translucent, thick stroke (alpha < 1 renders with multiply blending). */
export const InkStrokeSchema = AnnotationBase.extend({
  kind: z.literal("stroke"),
  /** legacy field from before pen and marker were one tool; kept so stored layers still parse */
  tool: z.enum(["pen", "highlighter"]).optional(),
  color: ColorSchema,
  /** pt */
  width: z.number().positive(),
  /** flat [x0, y0, p0, x1, y1, p1, ...] in slide pt, pressure 0..1 */
  points: z.array(z.number()),
});
export type InkStroke = z.infer<typeof InkStrokeSchema>;

export const ShapeAnnotationSchema = AnnotationBase.extend({
  kind: z.literal("shape"),
  shape: z.enum(["rect", "ellipse", "line", "arrow"]),
  /** top-left for rect/ellipse; start point for line/arrow (w/h are then signed deltas) */
  x: z.number(),
  y: z.number(),
  w: z.number(),
  h: z.number(),
  color: ColorSchema,
  width: z.number().positive(),
  fill: z.boolean().default(false),
});
export type ShapeAnnotation = z.infer<typeof ShapeAnnotationSchema>;

export const TextAnnotationSchema = AnnotationBase.extend({
  kind: z.literal("text"),
  x: z.number(),
  y: z.number(),
  /** wrap width in pt */
  w: z.number().positive(),
  text: z.string(),
  color: ColorSchema,
  /** font size in pt */
  size: z.number().positive(),
});
export type TextAnnotation = z.infer<typeof TextAnnotationSchema>;

/** A PNG, JPEG, WebP, GIF or SVG sent through the draw API, drawn into the box (aspect ratio kept). */
export const ImageAnnotationSchema = AnnotationBase.extend({
  kind: z.literal("image"),
  x: z.number(),
  y: z.number(),
  w: z.number().positive(),
  h: z.number().positive(),
  /** same-origin path of the stored file, /api/decks/<deck>/drawings/<file> */
  src: z.string(),
  mime: z.string(),
});
export type ImageAnnotation = z.infer<typeof ImageAnnotationSchema>;

const AnnotationUnion = z.discriminatedUnion("kind", [InkStrokeSchema, ShapeAnnotationSchema, TextAnnotationSchema, ImageAnnotationSchema]);
/** Old layers stored strokes without `kind`. */
export const AnnotationSchema = z.preprocess(
  (v) => (v && typeof v === "object" && !("kind" in v) ? { ...(v as object), kind: "stroke" } : v),
  AnnotationUnion,
);
export type Annotation = z.infer<typeof AnnotationUnion>;

export const StrokesLayerSchema = z.object({ strokes: z.array(AnnotationSchema).default([]) });
export type StrokesLayer = z.infer<typeof StrokesLayerSchema>;

/** Notes layer. Imported from the notes slide, editable live by the presenter. */
export const NotesLayerSchema = z.object({
  body: TextBodySchema.nullable().default(null),
  plain: z.string().default(""),
});
export type NotesLayer = z.infer<typeof NotesLayerSchema>;

/** Everything the room persists per deck (annotations and notes per page, keyed by page id). */
export const LayersFileSchema = z.object({
  version: z.literal(1).default(1),
  strokes: z.record(z.array(AnnotationSchema)).default({}),
  notes: z.record(z.string()).default({}),
});
export type LayersFile = z.infer<typeof LayersFileSchema>;
